import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react'
import { ArrowDown, FilePlus, MessageSquareOff } from 'lucide-react'
import type { Bericht, BeurtStand, GesprekMeta, Voorstel } from '../../../gedeeld/types'
import { c, Fout } from '../../lib/api'
import { useBeurt } from '../../lib/beurt'
import { navigeer, useTitel } from '../../lib/router'
import { gesprekBijwerken, laadGesprekken, lees, meldFout, useMag, useStaat, zet } from '../../lib/staat'
import { BestandenPaneel } from './Bestanden'
import { useBijlagen } from './useBijlagen'
import { Knop, Laden, Leeg } from '../ui'
import { AssistentBericht, Denkt, GebruikerBericht } from './Berichten'
import Invoer from './Invoer'
import Praten from './Praten'
import { kanPraten, wekStem } from '../../lib/spraak'
import Welkom from './Welkom'

/* ===========================================================================
 *  Een gesprek met Cowork
 *
 *  /       een nieuw gesprek: de begroeting en de voorbeeldvragen
 *  /c/:id  een bestaand gesprek
 *
 *  Het is hetzelfde onderdeel, en het blijft staan als het adres na de
 *  eerste vraag van / naar /c/<id> springt (zie App.tsx): de vraag die net
 *  loopt hoort bij dit scherm, en opnieuw ophalen zou hem een tel laten
 *  verdwijnen.
 *
 *  Hoe een vraag verloopt
 *  ----------------------
 *
 *  vraag   →  het gesprek (nieuw of bestaand), jouw bericht, en een beurt
 *  stand   →  om de paar seconden, tot de beurt klaar, mislukt of gestopt is
 *              (zie lib/beurt.ts)
 *  klaar   →  het antwoord komt in het gesprek en wordt even uitgeschreven
 *
 *  Wie weggaat en terugkomt terwijl een vraag loopt, krijgt van de actie
 *  gesprek een `lopend` mee, en dit scherm volgt die gewoon verder.
 *
 *  Bestanden: sleep ze op het hele gesprek, plak ze, of gebruik de
 *  paperclip (zie useBijlagen.ts en Invoer.tsx). Ze gaan als `bijlagen` mee
 *  met de vraag en worden niet bewaard.
 * =========================================================================== */

let lokaalTeller = 0
const lokaalId = () => `lokaal-${++lokaalTeller}`
const isLokaal = (b: Bericht) => b.id.startsWith('lokaal-')

/** Hoe dicht bij de onderkant nog "onderaan" telt. */
const ONDER_MARGE = 96

export default function Chat({ id }: { id: string | null }) {
  const [meta, setMeta] = useState<GesprekMeta | null>(null)
  const [berichten, setBerichten] = useState<Bericht[]>([])
  const [laden, setLaden] = useState(false)
  const [laadFout, setLaadFout] = useState<{ tekst: string; bestaatNiet: boolean } | null>(null)
  const [concept, setConcept] = useState('')
  const [verstuurt, setVerstuurt] = useState(false)
  /* Het antwoord dat net binnenkwam en even wordt uitgeschreven. */
  const [onthul, setOnthul] = useState<string | null>(null)
  const [onder, setOnder] = useState(true)
  /* Opnieuw proberen na een mislukte laadpoging. */
  const [poging, setPoging] = useState(0)

  /* Het gesprek dat dit scherm nu toont. Een ref, omdat de beurt-hook en
     de antwoorden van de server er na een await nog naar kijken. */
  const huidig = useRef<string | null>(id)
  /* Een gesprek dat we zelf net aanmaakten; het adres springt erheen, en
     dat is dan geen reden om opnieuw te laden. */
  const zelfGemaakt = useRef<string | null>(null)
  const laadNr = useRef(0)

  const bijlagen = useBijlagen(useCallback(() => huidig.current, []))
  /* Recht cowork.bijlagen (0198): zonder geen paperclip en geen slepen. */
  const magBijlagen = useMag('cowork.bijlagen')
  /* Praten (0201): het recht, en een browser die het kan. */
  const magPraten = useMag('cowork.spraak') && kanPraten()
  const [praten, setPraten] = useState(false)
  /* Een gesproken vraag wacht op zijn antwoord; zie vraagGesproken. */
  const opAntwoord = useRef<((b: Bericht | null) => void) | null>(null)
  const paneel = useStaat((s) => s.bestandenPaneel)
  /* Slepen: dragenter en dragleave komen ook van elk kind binnen het vlak;
     tellen is de enige betrouwbare manier om te weten of je er nog boven bent. */
  const [sleept, setSleept] = useState(false)
  const sleepTeller = useRef(0)

  const scroll = useRef<HTMLDivElement>(null)
  const kolom = useRef<HTMLDivElement>(null)
  const vastOnder = useRef(true)

  /* De titel kan veranderen terwijl je kijkt (de server bedenkt er een na
     het eerste antwoord); de zijbalk weet de nieuwste. */
  const zijTitel = useStaat((s) => (id ? s.gesprekken.find((g) => g.id === id)?.titel : undefined))
  useTitel(id ? (zijTitel ?? meta?.titel ?? (laden ? '' : 'Gesprek')) : 'Nieuw gesprek')

  const beurt = useBeurt(useCallback((b: BeurtStand) => {
    if (b.gesprekId && huidig.current && b.gesprekId !== huidig.current) return
    let antwoord: Bericht | null = b.bericht ?? null
    if (!antwoord && b.status === 'mislukt') {
      antwoord = { id: lokaalId(), rol: 'assistent', tekst: b.fout ?? 'Cowork kwam er niet uit. Probeer het opnieuw.', at: Date.now(), fout: true }
    }
    if (!antwoord && b.status === 'gestopt') {
      antwoord = { id: lokaalId(), rol: 'assistent', tekst: '_Gestopt voordat er een antwoord was._', at: Date.now() }
    }
    if (antwoord) {
      const a = antwoord
      setBerichten((x) => [...x.filter((y) => y.id !== a.id), a])
      if (!a.fout && b.status === 'klaar') setOnthul(a.id)
    }
    /* Het spraakscherm wacht hierop om het voor te lezen. */
    opAntwoord.current?.(antwoord)
    opAntwoord.current = null
    /* De titel en de volgorde in de zijbalk: na een antwoord kan de server
       het gesprek een betere naam hebben gegeven. */
    void laadGesprekken()
  }, []))

  /* ---------------- een ander gesprek ---------------- */

  useEffect(() => {
    if (id && id === zelfGemaakt.current) {
      zelfGemaakt.current = null
      return
    }
    huidig.current = id
    beurt.wis()
    /* Praten hoort bij het gesprek waarin het begon. Ging je weg (de
       terugknop, een link), dan kwam het antwoord nooit meer bij het
       spraakscherm aan -- dat bleef op "Ik zoek het op…" staan. */
    opAntwoord.current?.(null)
    opAntwoord.current = null
    setPraten(false)
    /* Bijlagen horen bij één vraag in één gesprek. */
    bijlagen.wisAlles()
    zet({ bestandenPaneel: false })
    setOnthul(null)
    setLaadFout(null)
    vastOnder.current = true
    setOnder(true)

    if (!id) {
      setMeta(null)
      setBerichten([])
      setLaden(false)
      return
    }

    const nr = ++laadNr.current
    setLaden(true)
    setBerichten([])
    c('gesprek', { id })
      .then(({ gesprek }) => {
        if (nr !== laadNr.current) return
        const { berichten: lijst, lopend, ...m } = gesprek
        setMeta(m)
        setBerichten(lijst)
        /* Een vraag die nog liep toen je wegging, of die net elders begon
           (Nu uitvoeren bij een terugkerende taak). */
        const elders = lees().teVolgen
        if (lopend) beurt.volg(lopend)
        else if (elders && elders.gesprekId === id) beurt.volg(elders)
        if (elders && elders.gesprekId === id) zet({ teVolgen: null })
      })
      .catch((e) => {
        if (nr !== laadNr.current) return
        setLaadFout({
          tekst: e instanceof Error ? e.message : String(e),
          bestaatNiet: e instanceof Fout && (e.status === 404 || e.status === 403),
        })
      })
      .finally(() => { if (nr === laadNr.current) setLaden(false) })
    /* beurt.volg en beurt.wis veranderen nooit; alleen het adres telt. */
  }, [id, poging])

  /* ---------------- vragen ---------------- */

  async function stuur(tekst: string, opties: { spraak?: boolean } = {}): Promise<boolean> {
    if (beurt.bezig || verstuurt || bijlagen.bezig || bijlagen.fout) return false
    /* Zonder tekst mag het als er bijlagen zijn: dan vraag je "kijk hier eens naar". */
    const n = bijlagen.klaar
    const t = tekst.trim() || (n ? (n === 1 ? 'Bekijk de bijlage.' : 'Bekijk de bijlagen.') : '')
    if (!t) return false
    const bij = huidig.current
    const mee = bijlagen.neem()
    const bestanden = mee.map((b) => b.bestand!).filter(Boolean)
    const lokaal: Bericht = { id: lokaalId(), rol: 'gebruiker', tekst: t, at: Date.now(), ...(bestanden.length ? { bestanden } : {}) }
    setBerichten((x) => [...x, lokaal])
    setConcept('')
    setVerstuurt(true)
    naarOnder(true)

    try {
      const uit = await c('vraag', {
        gesprekId: bij, tekst: t, ...(bestanden.length ? { bijlagen: bestanden.map((b) => b.id) } : {}),
        ...(opties.spraak ? { spraak: true } : {}),
      })
      gesprekBijwerken(uit.gesprek)
      if (huidig.current !== bij) return false
      setBerichten((x) => x.map((b) => (b.id === lokaal.id ? uit.vraag : b)))
      setMeta(uit.gesprek)
      if (!bij) {
        huidig.current = uit.gesprek.id
        zelfGemaakt.current = uit.gesprek.id
        navigeer(`/c/${uit.gesprek.id}`, true)
      }
      beurt.volg(uit.beurt)
      return true
    } catch (e) {
      /* Niet aangekomen: de vraag terug in het veld, zodat je hem niet
         opnieuw hoeft te typen. */
      if (huidig.current === bij) {
        setBerichten((x) => x.filter((b) => b.id !== lokaal.id))
        setConcept((c) => c || tekst.trim())
        bijlagen.zetTerug(mee)
      }
      meldFout(e)
      return false
    } finally {
      setVerstuurt(false)
    }
  }

  /** Een gesproken vraag (0201): stellen en wachten tot het antwoord er is. */
  function vraagGesproken(tekst: string): Promise<Bericht | null> {
    return new Promise((klaar) => {
      opAntwoord.current = klaar
      void stuur(tekst, { spraak: true }).then((gelukt) => {
        if (!gelukt && opAntwoord.current === klaar) { opAntwoord.current = null; klaar(null) }
      })
    })
  }

  async function opnieuw() {
    const g = huidig.current
    if (!g || beurt.bezig) return
    try {
      const uit = await c('opnieuw', { gesprekId: g })
      if (huidig.current !== g) return
      setBerichten((x) => {
        let y = uit.weg ? x.filter((b) => b.id !== uit.weg) : x
        /* Een foutmelding die alleen hier stond, hoort ook weg. */
        while (y.length && y[y.length - 1]!.rol === 'assistent' && isLokaal(y[y.length - 1]!)) y = y.slice(0, -1)
        return y
      })
      setOnthul(null)
      naarOnder(true)
      beurt.volg(uit.beurt)
    } catch (e) {
      meldFout(e)
    }
  }

  function voorstelBijgewerkt(berichtId: string, v: Voorstel) {
    setBerichten((x) => x.map((b) => (b.id === berichtId
      ? { ...b, voorstellen: b.voorstellen?.map((o) => (o.id === v.id ? v : o)) }
      : b)))
  }

  /* ---------------- meescrollen ---------------- */

  function naarOnder(dwing = false) {
    const s = scroll.current
    if (!s) return
    if (dwing) vastOnder.current = true
    if (!vastOnder.current) return
    s.scrollTop = s.scrollHeight
    setOnder(true)
  }

  /* Alles wat groeit (een nieuw bericht, het uitschrijven, een stap die
     erbij komt) laat de kolom groeien; zolang je onderaan stond, gaan we
     mee. Heb je zelf omhoog gescrold, dan blijft het staan en verschijnt
     er een knop om terug te gaan. */
  useLayoutEffect(() => {
    const k = kolom.current
    if (!k) return
    /* Niet bij de begroeting: die lees je van boven af. */
    const ro = new ResizeObserver(() => { if (!k.classList.contains('leeg')) naarOnder() })
    ro.observe(k)
    return () => ro.disconnect()
  }, [])

  function opScroll() {
    const s = scroll.current
    if (!s) return
    const dichtbij = s.scrollHeight - s.scrollTop - s.clientHeight < ONDER_MARGE
    vastOnder.current = dichtbij
    if (dichtbij !== onder) setOnder(dichtbij)
  }

  /* ---------------- bestanden ---------------- */

  /* De paperclip in de kop telt wat er in dit gesprek aan bestanden staat. */
  const aantalBestanden = new Set(berichten.flatMap((b) => b.bestanden?.map((x) => x.id) ?? [])).size
  useEffect(() => {
    zet({ bestandenTeller: id ? aantalBestanden : null })
  }, [id, aantalBestanden])
  useEffect(() => () => zet({ bestandenTeller: null, bestandenPaneel: false }), [])

  const heeftBestanden = (e: DragEvent) => [...e.dataTransfer.types].includes('Files')
  const sleepHandlers = {
    onDragEnter: (e: DragEvent) => {
      if (!heeftBestanden(e)) return
      e.preventDefault()
      sleepTeller.current++
      setSleept(true)
    },
    onDragOver: (e: DragEvent) => {
      if (!heeftBestanden(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave: (e: DragEvent) => {
      if (!heeftBestanden(e)) return
      sleepTeller.current = Math.max(0, sleepTeller.current - 1)
      if (!sleepTeller.current) setSleept(false)
    },
    onDrop: (e: DragEvent) => {
      if (!heeftBestanden(e)) return
      e.preventDefault()
      sleepTeller.current = 0
      setSleept(false)
      const f = [...e.dataTransfer.files]
      if (f.length && !magBijlagen) {
        meldFout(new Error('Bijlagen meesturen staat voor jou uit; het management zet dat aan bij je rechten.'))
      } else if (f.length) {
        bijlagen.voegToe(f)
        window.dispatchEvent(new Event('cowork:invoer'))
      }
    },
  }

  /* ---------------- tekenen ---------------- */

  const leeg = !id && !berichten.length
  const laatsteAssistent = [...berichten].reverse().find((b) => b.rol === 'assistent')?.id

  return (
    <div className="chat" {...sleepHandlers}>
      {sleept && (
        <div className="sleep-vlak" aria-hidden>
          <div className="sleep-kader">
            <FilePlus size={28} />
            <strong>Laat los om toe te voegen</strong>
            <span>PDF, Excel, Word, PowerPoint, CSV, tekst, foto's of ZIP, tot 15 MB. Ze worden niet bewaard.</span>
          </div>
        </div>
      )}
      {paneel && id && <BestandenPaneel gesprekId={id} onSluit={() => zet({ bestandenPaneel: false })} />}

      <div className="chat-scroll" id="inhoud" ref={scroll} onScroll={opScroll}>
        <div className={`chat-kolom ${leeg ? 'leeg' : ''}`} ref={kolom}>
          {leeg && <Welkom onVraag={(v) => void stuur(v)} />}

          {laden && <Laden tekst="Gesprek ophalen…" />}

          {laadFout && (
            <Leeg icoon={<MessageSquareOff />} titel={laadFout.bestaatNiet ? 'Dit gesprek is er niet (meer)' : 'Het gesprek kwam niet binnen'}
              tekst={laadFout.bestaatNiet ? 'Misschien is het verwijderd, of is het van iemand anders.' : laadFout.tekst}>
              {!laadFout.bestaatNiet && <Knop onClick={() => setPoging((x) => x + 1)}>Opnieuw</Knop>}
              <Knop soort="hoofd" onClick={() => navigeer('/')}>Nieuw gesprek</Knop>
            </Leeg>
          )}

          {berichten.map((b) => (b.rol === 'gebruiker'
            ? <GebruikerBericht key={b.id} b={b} verstuurt={isLokaal(b) && verstuurt} />
            : (
              <AssistentBericht
                key={b.id}
                b={b}
                laatste={b.id === laatsteAssistent && !beurt.bezig}
                onthul={onthul === b.id}
                onOnthuld={() => setOnthul((x) => (x === b.id ? null : x))}
                onOpnieuw={() => void opnieuw()}
                bezig={beurt.bezig || verstuurt}
                onVoorstel={(v) => voorstelBijgewerkt(b.id, v)}
              />
            )))}

          {beurt.stand && <Denkt stand={beurt.stand} />}
        </div>
      </div>

      <div className="chat-voet">
        {!onder && !leeg && (
          <button type="button" className="naar-onder" aria-label="Naar het nieuwste bericht" onClick={() => {
            const s = scroll.current
            vastOnder.current = true
            s?.scrollTo({ top: s.scrollHeight, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
          }}>
            <ArrowDown size={16} />
          </button>
        )}

        <Invoer
          waarde={concept}
          onWaarde={setConcept}
          onStuur={() => void stuur(concept)}
          bezig={beurt.bezig || verstuurt}
          onStop={() => void beurt.stop()}
          stoppen={beurt.stoppen || (verstuurt && !beurt.bezig)}
          bijlagen={bijlagen.lijst}
          onBestanden={bijlagen.voegToe}
          magBijlagen={magBijlagen}
          onPraten={magPraten ? () => { wekStem(); setPraten(true) } : undefined}
          onWeg={bijlagen.weg}
          uploadBezig={bijlagen.bezig}
          uploadFout={bijlagen.fout}
        />
        {praten && <Praten vraag={vraagGesproken} onSluit={() => { opAntwoord.current = null; setPraten(false) }} />}
        <p className="invoer-noot" id="invoer-noot">
          Cowork zoekt alleen in gegevens waar jij recht op hebt en draait op de eigen AI op kantoor. Controleer belangrijke cijfers.
        </p>
      </div>
    </div>
  )
}
